export function getDayDates(date: Date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function getHourDates(date: Date) {
  // Odečíst minuty/sekundy na ose epochy, NE setMinutes(0): ten skládá nástěnný čas a v opakované
  // hodině při podzimní změně (02:00–02:59 dvakrát) vrátí začátek té PRVNÍ → konec < začátek.
  const start = new Date(date.getTime() - (date.getMinutes() * 60_000 + date.getSeconds() * 1000 + date.getMilliseconds()));
  const end = new Date(start.getTime() + 60 * 60 * 1000 - 1);
  return { start, end };
}

export function calculateStats(messages: HolyMQTT, start: Date, end: Date) {
  // get type of values
  const valueType = messages.topic?.valueType;
  const topicId = messages.values[0].topicId;

  // BOOLEAN
  let uptime: number | null = null;
  let downtime: number | null = null;
  let risingCount: number | null = null;
  let fallingCount: number | null = null;

  if (valueType === "BOOLEAN") {
    messages.values.unshift({
      ...messages.values[0],
      timestamp: start,
    });
    messages.values.push({
      ...messages.values[messages.values.length - 1],
      timestamp: end,
    });

    // get duration the value was true
    uptime = 0;
    downtime = 0;
    risingCount = 0;
    fallingCount = 0;

    let lastUpTimestamp = null;
    let lastDownTimestamp = null;
    for (let i = 0; i < messages.values.length; i++) {
      if (i > 0) {
        // rising count
        if (messages.values[i].value === true && messages.values[i - 1].value === false) {
          risingCount++;
        }
        // falling count
        if (messages.values[i].value === false && messages.values[i - 1].value === true) {
          fallingCount++;
        }
      }

      // uptime
      if (messages.values[i].value === true && !lastUpTimestamp) {
        lastUpTimestamp = messages.values[i].timestamp.getTime();
        continue;
      }
      if (
        (messages.values[Math.min(i + 1, messages.values.length - 1)].value === false ||
          (i === messages.values.length - 1 && messages.values[i].value === true)) &&
        lastUpTimestamp
      ) {
        uptime += messages.values[i].timestamp.getTime() - lastUpTimestamp;
        lastUpTimestamp = null;
        continue;
      }

      // downtime
      if (messages.values[i].value === false && !lastDownTimestamp) {
        lastDownTimestamp = messages.values[i].timestamp.getTime();
        continue;
      }
      if (
        (messages.values[Math.min(i + 1, messages.values.length - 1)].value === true ||
          (i === messages.values.length - 1 && messages.values[i].value === false)) &&
        lastDownTimestamp
      ) {
        downtime += messages.values[i].timestamp.getTime() - lastDownTimestamp;
        lastDownTimestamp = null;
        continue;
      }
    }
  }

  // FLOAT
  let min: number | null = null;
  let max: number | null = null;
  let avg: number | null = null;
  let count: number | null = null;

  if (valueType === "FLOAT") {
    // Time-weighted statistics (intervals between samples may be irregular)
    // Clone array so we don't mutate original for other consumers
    const floatValues = [...messages.values];

    // Ensure we have boundary points at start and end so final interval spans the whole day.
    // If first sample is after start, assume its value held since start.
    if (floatValues.length > 0 && floatValues[0].timestamp.getTime() > start.getTime()) {
      floatValues.unshift({ ...floatValues[0], timestamp: new Date(start) });
    }
    // If last sample is before end, assume its value holds until end.
    if (floatValues.length > 0 && floatValues[floatValues.length - 1].timestamp.getTime() < end.getTime()) {
      floatValues.push({ ...floatValues[floatValues.length - 1], timestamp: new Date(end) });
    }

    // Guard: if still no values, leave stats null
    if (floatValues.length > 0) {
      // Compute min/max across actual observed samples (excluding synthetic boundaries)
      const observedVals = messages.values.map((m) => m.value as number);
      min = Math.min(...observedVals);
      max = Math.max(...observedVals);
      count = messages.values.length;

      // Weighted average: sum(value * duration) / sum(duration)
      let weightedSum = 0;
      let totalDuration = 0;
      for (let i = 0; i < floatValues.length - 1; i++) {
        const current = floatValues[i];
        const next = floatValues[i + 1];
        const duration = next.timestamp.getTime() - current.timestamp.getTime();
        if (duration <= 0) continue; // skip zero or negative intervals (shouldn't happen if sorted)
        weightedSum += (current.value as number) * duration;
        totalDuration += duration;
      }
      if (totalDuration > 0) {
        avg = weightedSum / totalDuration;
      } else {
        // Fallback: single point or invalid durations
        avg = floatValues[0].value as number;
      }
    }
  }

  return {
    topic: topicId,
    valueType,
    date: start,

    uptime,
    downtime,
    risingCount,
    fallingCount,
    min,
    max,
    avg,
    count,
    // Client-side calculateStats nepočítá rozšířené percentily / stddev —
    // ty doplňuje teprve DB-side StatWindow agregace na serveru.
    median: null,
    p05: null,
    p95: null,
    stddev: null,
  } as dailyStats;
}
